import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { shouldReloadDraftForKeyChange } from "./createEventDraftKey";
import type { DayOfWeek, GroupHostNotificationMode, RecurringWindowRequest } from "@/services/types";
import { useAuth } from "@/state/AuthContext";
import type { CalendarProviderId, ConferencingProviderId } from "@/lib/providerIds";
import { toCanonicalProviderId } from "@/lib/providerIds";
import type { SupportedEventTypeKind } from "@/features/event-types/eventTypeCatalog";

export type ConferencingProvider = ConferencingProviderId;
export type OrchestrationProvider = CalendarProviderId;

export interface AvailabilityCalendarBindingDraft {
  provider: string;
  calendarId: string;
}

export interface CreateEventDraft {
  eventKind: SupportedEventTypeKind;
  capacity: number;
  groupHostNotificationMode: GroupHostNotificationMode;
  eventName: string;
  description: string;
  location: string;
  duration: number;
  weeklyRules: Record<DayOfWeek, { enabled: boolean; startTime: string; endTime: string }>;
  availabilityMode: "inherit" | "custom";
  currentStep: number;
  touchedSteps: number[];
  orchestrationProvider: OrchestrationProvider | "";
  availabilityCalendarBindings: AvailabilityCalendarBindingDraft[];
  conferencingProvider: ConferencingProvider;
  customConferenceUrl: string;
  selectedParticipantIds: string[];
  groupReservationWindows: RecurringWindowRequest[];
  paymentEnabled: boolean;
  paymentConnectionId: string;
  paymentPriceUsd: string;
}

const DAYS: DayOfWeek[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

const defaultDraft: CreateEventDraft = {
  eventKind: "ONE_ON_ONE",
  capacity: 1,
  groupHostNotificationMode: "SMART_SUMMARY",
  eventName: "30-min Intro",
  description: "",
  location: "default",
  duration: 30,
  currentStep: 0,
  touchedSteps: [0],
  orchestrationProvider: "",
  availabilityCalendarBindings: [],
  weeklyRules: DAYS.reduce((acc, day) => {
    acc[day] = { enabled: day !== "SATURDAY" && day !== "SUNDAY", startTime: "09:00", endTime: "17:00" };
    return acc;
  }, {} as Record<DayOfWeek, { enabled: boolean; startTime: string; endTime: string }>),
  availabilityMode: "inherit",
  conferencingProvider: "default",
  customConferenceUrl: "",
  selectedParticipantIds: [],
  groupReservationWindows: [],
  paymentEnabled: false,
  paymentConnectionId: "",
  paymentPriceUsd: "",
};

interface CreateEventStateValue {
  draft: CreateEventDraft;
  setDraft: React.Dispatch<React.SetStateAction<CreateEventDraft>>;
  goToStep: (index: number) => void;
  setCurrentStep: (index: number) => void;
  reset: () => void;
}

const CreateEventContext = createContext<CreateEventStateValue | null>(null);

// Sessions persisted before the redesign may still carry "google_meet" / "microsoft_teams" — an event
// type can no longer pin either, so those drafts collapse onto the "default" pointer.
function migrateConferencingProvider(raw: unknown): ConferencingProvider {
  const token = toCanonicalProviderId(String(raw ?? ""));
  if (token === "zoom") return "zoom";
  if (token === "custom" || token === "custom_url") return "custom_url";
  if (token === "none" || token === "phone" || token === "in_person" || token === "in-person") return "none";
  return "default";
}

function migrateOrchestrationProvider(raw: unknown): OrchestrationProvider | "" {
  const token = toCanonicalProviderId(String(raw ?? ""));
  if (token === "google") return "google";
  if (token === "microsoft") return "microsoft";
  if (token === "GOOGLE") return "google";
  if (token === "MICROSOFT") return "microsoft";
  return "";
}

function mergeDraft(raw: unknown): CreateEventDraft {
  const partial = (raw && typeof raw === "object" ? raw : {}) as Partial<CreateEventDraft>;
  return {
    ...defaultDraft,
    ...partial,
    eventKind: partial.eventKind === "GROUP" ? "GROUP" : partial.eventKind === "ROUND_ROBIN" ? "ROUND_ROBIN" : partial.eventKind === "COLLECTIVE" ? "COLLECTIVE" : "ONE_ON_ONE",
    capacity: typeof partial.capacity === "number" && Number.isFinite(partial.capacity) ? partial.capacity : defaultDraft.capacity,
    groupHostNotificationMode:
      partial.groupHostNotificationMode === "EVERY_REGISTRATION"
      || partial.groupHostNotificationMode === "DAILY_DIGEST"
      || partial.groupHostNotificationMode === "IMPORTANT_ONLY"
      || partial.groupHostNotificationMode === "NONE"
        ? partial.groupHostNotificationMode
        : "SMART_SUMMARY",
    availabilityMode: partial.availabilityMode === "custom" ? "custom" : "inherit",
    conferencingProvider: migrateConferencingProvider(partial.conferencingProvider),
    orchestrationProvider: migrateOrchestrationProvider(partial.orchestrationProvider),
    availabilityCalendarBindings: Array.isArray(partial.availabilityCalendarBindings)
      ? partial.availabilityCalendarBindings
          .filter((item) => item && typeof item === "object")
          .map((item) => {
            const raw = item as unknown as Record<string, unknown>;
            return {
              provider: String(raw.provider ?? "").toLowerCase(),
              calendarId: String(raw.calendarId ?? ""),
            };
          })
          .filter((item) => item.provider && item.calendarId)
      : [],
    selectedParticipantIds: Array.isArray((partial as { selectedParticipantIds?: unknown[] }).selectedParticipantIds)
      ? ((partial as { selectedParticipantIds?: unknown[] }).selectedParticipantIds as unknown[])
          .filter((id): id is string => typeof id === "string" && id.trim().length > 0)
      : [],
  };
}

export function CreateEventProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const key = `create-event-draft:${user?.id ?? "anon"}`;
  const [draft, setDraft] = useState<CreateEventDraft>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? mergeDraft(JSON.parse(raw)) : defaultDraft;
    } catch {
      return defaultDraft;
    }
  });
  const loadedKeyRef = useRef(key);

  useEffect(() => {
    const previousKey = loadedKeyRef.current;
    loadedKeyRef.current = key;
    if (!shouldReloadDraftForKeyChange(previousKey, key)) {
      return;
    }
    try {
      const raw = sessionStorage.getItem(key);
      setDraft(raw ? mergeDraft(JSON.parse(raw)) : defaultDraft);
    } catch {
      setDraft(defaultDraft);
    }
  }, [key]);

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(draft));
    } catch {
      // no-op
    }
  }, [draft, key]);

  const value = useMemo<CreateEventStateValue>(() => ({
    draft,
    setDraft,
    goToStep: (index: number) => setDraft((prev) => ({ ...prev, currentStep: index, touchedSteps: prev.touchedSteps.includes(index) ? prev.touchedSteps : [...prev.touchedSteps, index] })),
    setCurrentStep: (index: number) => setDraft((prev) => ({ ...prev, currentStep: index })),
    reset: () => setDraft(defaultDraft),
  }), [draft]);

  return <CreateEventContext.Provider value={value}>{children}</CreateEventContext.Provider>;
}

export function useCreateEventState() {
  const ctx = useContext(CreateEventContext);
  if (!ctx) throw new Error("useCreateEventState must be used within CreateEventProvider");
  return ctx;
}
